import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mic, Square, Play, Pause, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { uploadDocument } from "@/lib/api";

/**
 * Voice-note recorder. Uses the browser MediaRecorder API to capture a short
 * audio clip and uploads it via `/api/upload-doc` as an audio/* attachment.
 * On success calls `onSave({path, filename, content_type, size})` so the
 * caller can attach it to a note's `attachments` array.
 */
export default function AudioRecorder({ open, onOpenChange, onSave }) {
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const mediaRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (!open) {
      // Reset when the modal closes
      try { mediaRef.current?.stop(); } catch {}
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setRecording(false); setBlob(null); setBlobUrl(null);
      setPlaying(false); setElapsed(0);
      chunksRef.current = [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Prefer the widely-supported `audio/webm` container.
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setBlob(b);
        setBlobUrl(URL.createObjectURL(b));
        stream.getTracks().forEach((t) => t.stop());
      };
      // Pass a 500ms timeslice so `ondataavailable` fires periodically.
      // Without this, some browsers (Safari on iOS, older Chrome) only
      // flush chunks AFTER .stop() which fails if stop is called too fast
      // and leaves a 0-byte blob (Whisper then rejects with "audio too
      // short"). 500ms is the sweet spot: short enough to always capture
      // the full clip, long enough to avoid chunk fragmentation.
      rec.start(500);
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      const inFrame = window.self !== window.top;
      const blocked = err?.name === "NotAllowedError" || err?.name === "SecurityError";
      if (blocked && inFrame) {
        toast.error("The microphone is blocked inside this preview window", {
          description: "Open the app in its own browser tab to record voice notes.",
          action: { label: "Open tab", onClick: () => window.open(window.location.href, "_blank", "noopener") },
        });
      } else if (err?.name === "NotFoundError") {
        toast.error("No microphone found on this device");
      } else if (blocked) {
        toast.error("Microphone access denied", { description: "Allow the microphone for this site in your browser settings, then try again." });
      } else {
        toast.error("Couldn't start the microphone — try again");
      }
      console.warn("[audio] getUserMedia failed:", err);
    }
  };

  const stopRecording = () => {
    // Guard: Whisper rejects clips shorter than 0.1 s and users have
    // repeatedly tapped Record → Stop too fast, getting an unhelpful
    // "transcription failed". Force a 2-second minimum so there's always
    // enough audio even on devices where MediaRecorder buffers the first
    // ~1 s before flushing. The timer keeps running until the user taps
    // Stop again after the 2 s mark.
    if (elapsed < 2) {
      toast.message("Hold on — recording needs at least 2 seconds");
      return;
    }
    try { mediaRef.current?.stop(); } catch {}
    setRecording(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const save = async () => {
    if (!blob) return;
    // Blob sanity check: MediaRecorder occasionally produces a header-only
    // blob (<2 KB) when the user's microphone has no audio or Safari
    // dropped the stream. Catch that here so Whisper doesn't reject it
    // later with a confusing "too short" error.
    if (blob.size < 2000) {
      toast.error("No audio captured — check your microphone and try again");
      setBlob(null); setBlobUrl(null); setElapsed(0);
      return;
    }
    try {
      setUploading(true);
      // Pick the right file extension so Whisper's format detector doesn't
      // choke on an iOS-Safari `audio/mp4` blob that was saved as `.webm`.
      const type = (blob.type || "").toLowerCase();
      const ext = type.includes("webm") ? "webm"
                : type.includes("mp4") || type.includes("m4a") ? "m4a"
                : type.includes("aac") ? "aac"
                : type.includes("ogg") ? "ogg"
                : type.includes("wav") ? "wav"
                : "webm";
      const name = `voice-${new Date().toISOString().replace(/[:.]/g, "-")}.${ext}`;
      const file = new File([blob], name, { type: blob.type || `audio/${ext}` });
      const data = await uploadDocument(file);
      // Save FAST — hand control back to the parent as soon as the audio is
      // uploaded. Transcription has been removed (users reported it was
      // unreliable across devices) — voice notes are now stored as plain
      // audio attachments and the user can type a title / caption manually.
      onSave({
        path: data.path,
        filename: data.filename || name,
        content_type: data.content_type || blob.type,
        size: data.size || blob.size,
      });
      onOpenChange(false);
    } catch (err) {
      toast.error("Could not save voice note");
      console.warn("[audio] upload failed:", err);
    } finally { setUploading(false); }
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="audio-recorder">
        <DialogHeader>
          <DialogTitle className="font-display">Voice note</DialogTitle>
        </DialogHeader>

        <div className="py-6 flex flex-col items-center gap-5">
          <div className={`h-24 w-24 rounded-full flex items-center justify-center transition-colors ${recording ? "bg-rose-500 animate-pulse" : blob ? "bg-emerald-500" : "bg-stone-200 dark:bg-stone-800"}`}>
            <Mic className={`h-10 w-10 ${recording || blob ? "text-white" : "text-stone-500"}`} />
          </div>

          <div className="font-mono text-2xl text-stone-900 dark:text-stone-100" data-testid="audio-timer">
            {fmt(elapsed)}
          </div>

          {blobUrl && !recording && (
            <div className="w-full flex items-center justify-center gap-2">
              <audio ref={audioRef} src={blobUrl} onEnded={() => setPlaying(false)} className="hidden" />
              <Button variant="outline" onClick={togglePlay} size="sm" data-testid="audio-play">
                {playing ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                {playing ? "Pause" : "Play"}
              </Button>
            </div>
          )}

          <div className="flex items-center gap-2 w-full">
            {!recording && !blob && (
              <Button onClick={startRecording} className="w-full rounded-full bg-rose-600 hover:bg-rose-700" data-testid="audio-start">
                <Mic className="h-4 w-4 mr-1" /> Start recording
              </Button>
            )}
            {recording && (
              <Button onClick={stopRecording} className="w-full rounded-full" variant="destructive" data-testid="audio-stop">
                <Square className="h-4 w-4 mr-1 fill-current" /> Stop
              </Button>
            )}
            {blob && !recording && (
              <>
                <Button variant="ghost" size="sm" onClick={() => { setBlob(null); setBlobUrl(null); setElapsed(0); }} className="flex-1" data-testid="audio-redo">
                  Redo
                </Button>
                <Button onClick={save} disabled={uploading} className="flex-1 rounded-full" data-testid="audio-save">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
